import { DefaultTool } from '@blocksuite/affine-block-surface';
import { DatabaseBlockModel } from '@blocksuite/affine-model';
import type { PointerEventState } from '@blocksuite/std';
import { BaseTool, type GfxModel } from '@blocksuite/std/gfx';

import { AutomationBlockModel } from '../schemas/automation-model.js';

type ElementType = 'database' | 'automation';

/**
 * Bridge to the Edge Rendering Service for preview edges.
 * Registered by EdgeOrchestrator on window.__edgePreviewBridge
 */
interface EdgePreviewBridge {
  addPreviewEdge(
    sourceId: string,
    cursorX: number,
    cursorY: number,
    type?: 'automation' | 'relation',
    automationDirection?: 'input' | 'output'
  ): void;
  updatePreviewEdge(cursorX: number, cursorY: number): void;
  snapPreviewEdge(targetId: string): void;
  commitPreviewEdge(): void;
  setConnectorHighlights(elementIds: string[]): void;
  setPreviewValidity(isValid: boolean): void;
  removePreviewEdge(): void;
  hasPreviewEdge(): boolean;
}

declare const window: Window & { __edgePreviewBridge?: EdgePreviewBridge };

/**
 * AutomationConnectorTool - Tool for creating edges between Databases and Automation blocks.
 *
 * Direction determines semantics:
 * - Database -> Automation = Input (sourceTableId) - only ONE allowed
 * - Automation -> Database = Output (added to outputTables) - multiple allowed
 *
 * Hotkey: 'W'
 *
 * @see sl-zfd for feature specification
 * @see sl-cmx for tool implementation spec
 * @see sl-db6 for connection handler spec
 */
export class AutomationConnectorTool extends BaseTool {
  static override toolName: string = 'automationConnector';

  private startElement: GfxModel | null = null;
  private startType: ElementType | null = null;
  private previewCommitted = false;

  /**
   * Gets the edge preview bridge from window.
   * Returns null if not available (EdgeOrchestrator not initialized).
   */
  private get bridge(): EdgePreviewBridge | null {
    return window.__edgePreviewBridge ?? null;
  }

  override activate(): void {
    // Set crosshair cursor to indicate connection mode
    this.gfx.cursor$.value = 'crosshair';
    this.previewCommitted = false;
  }

  override deactivate(): void {
    // Reset cursor
    this.gfx.cursor$.value = 'default';

    // Remove any active preview edge
    if (!this.previewCommitted) this.bridge?.removePreviewEdge();
    this.bridge?.setConnectorHighlights([]);

    this.startElement = null;
    this.startType = null;
  }

  override pointerMove(e: PointerEventState): void {
    const hovered = this.getElementAtPoint(e.x, e.y);
    if (!this.startElement || !this.startType) {
      this.bridge?.setConnectorHighlights(hovered ? [hovered.id] : []);
      return;
    }
    const targetType = hovered ? this.getElementType(hovered) : null;
    const valid =
      targetType !== null && this.isValidConnection(this.startType, targetType);
    this.bridge?.setConnectorHighlights(
      valid && hovered
        ? [this.startElement.id, hovered.id]
        : [this.startElement.id]
    );
  }

  override pointerDown(e: PointerEventState): void {
    const element = this.getElementAtPoint(e.x, e.y);
    if (!element) {
      // Clicked on empty space - just ignore, don't deactivate
      // Tool stays active until ESC or successful connection
      return;
    }

    const type = this.getElementType(element);
    if (!type) {
      // Clicked on invalid element - return to default tool
      console.log(
        '[AutomationConnectorTool] Invalid start element, must be Database or Automation'
      );
      this.gfx.tool.setTool(DefaultTool);
      return;
    }

    this.startElement = element;
    this.startType = type;
    this.bridge?.setConnectorHighlights([element.id]);
    console.log(`[AutomationConnectorTool] Start: ${type} (${element.id})`);
  }

  override dragStart(e: PointerEventState): void {
    if (!this.startElement) return;

    // Start preview edge via bridge
    this.bridge?.addPreviewEdge(
      this.startElement.id,
      e.x,
      e.y,
      'automation',
      this.startType === 'database' ? 'input' : 'output'
    );
  }

  override dragMove(e: PointerEventState): void {
    if (!this.startElement) return;

    const targetElement = this.getElementAtPoint(e.x, e.y);
    const targetType = targetElement
      ? this.getElementType(targetElement)
      : null;

    // Check if target is valid
    const isValid =
      targetType !== null &&
      this.startType !== null &&
      this.isValidConnection(this.startType, targetType);

    // Update preview edge position and validity via bridge
    if (isValid && targetElement) {
      this.bridge?.snapPreviewEdge(targetElement.id);
      this.bridge?.setConnectorHighlights([
        this.startElement.id,
        targetElement.id,
      ]);
    } else {
      this.bridge?.updatePreviewEdge(e.x, e.y);
      this.bridge?.setConnectorHighlights([this.startElement.id]);
    }
    this.bridge?.setPreviewValidity(isValid);
  }

  override dragEnd(e: PointerEventState): void {
    if (!this.startElement || !this.startType) {
      this.gfx.tool.setTool(DefaultTool);
      return;
    }

    const targetElement = this.getElementAtPoint(e.x, e.y);
    if (!targetElement) {
      console.log('[AutomationConnectorTool] No target element');
      this.resetAndReturn();
      return;
    }

    const targetType = this.getElementType(targetElement);
    if (!targetType) {
      console.log(
        '[AutomationConnectorTool] Invalid target, must be Database or Automation'
      );
      this.resetAndReturn();
      return;
    }

    // Validate: must be opposite types
    if (!this.isValidConnection(this.startType, targetType)) {
      console.log('[AutomationConnectorTool] Invalid connection: same type');
      this.resetAndReturn();
      return;
    }

    // Resolve the final pointer sample through the same target-bound routing
    // even if no dragMove event was emitted immediately before pointer-up.
    this.bridge?.snapPreviewEdge(targetElement.id);

    // Create the connection (async for steal dialog), then reset
    this.createConnection(this.startElement, targetElement, this.startType)
      .then(() => {
        this.previewCommitted = true;
        this.bridge?.commitPreviewEdge();
        this.resetAndReturn(true);
      })
      .catch(error => {
        console.error(error);
        this.resetAndReturn();
      });
  }

  override click(e: PointerEventState): void {
    // If we have a start element, try to complete on click
    if (this.startElement && this.startType) {
      const targetElement = this.getElementAtPoint(e.x, e.y);
      if (targetElement && targetElement !== this.startElement) {
        const targetType = this.getElementType(targetElement);
        if (targetType && this.isValidConnection(this.startType, targetType)) {
          // Create the connection (async for steal dialog), then reset
          this.createConnection(
            this.startElement,
            targetElement,
            this.startType
          )
            .then(() => this.resetAndReturn())
            .catch(console.error);
          return;
        }
      }
      // Invalid target or same element - reset
      this.resetAndReturn();
      return;
    }
    // No start element - don't reset, tool stays active
    // This allows the tool to be activated via button click without immediately deactivating
  }

  // =========================================================================
  // Helper Methods
  // =========================================================================

  private getElementAtPoint(x: number, y: number): GfxModel | null {
    const [modelX, modelY] = this.gfx.viewport.toModelCoord(x, y);
    const elements = this.gfx.getElementByPoint(modelX, modelY, { all: true });
    // Return first valid element (Database or Automation)
    for (const element of elements) {
      if (this.getElementType(element)) {
        return element;
      }
    }
    return null;
  }

  private getElementType(element: GfxModel): ElementType | null {
    if (element instanceof DatabaseBlockModel) {
      return 'database';
    }
    if (element instanceof AutomationBlockModel) {
      return 'automation';
    }
    return null;
  }

  private isValidConnection(
    startType: ElementType,
    targetType: ElementType
  ): boolean {
    return startType !== targetType;
  }

  private async createConnection(
    start: GfxModel,
    target: GfxModel,
    startType: ElementType
  ): Promise<void> {
    if (startType === 'database') {
      const database = start as DatabaseBlockModel;
      await this.setInputTable(target as AutomationBlockModel, database);
    } else if (startType === 'automation') {
      const automation = start as AutomationBlockModel;
      const database = target as DatabaseBlockModel;
      this.addOutputTable(automation, database);
    }
  }

  /**
   * Set the input table for an automation (Database -> Automation).
   *
   * sl-m379: No more steal dialog. Logic:
   * - No lead table → set as lead (sourceTableId)
   * - Same DB already connected → no-op
   * - Lead exists, different DB → add as secondary (inputTables)
   */
  private async setInputTable(
    automation: AutomationBlockModel,
    database: DatabaseBlockModel
  ): Promise<void> {
    const existingInput = automation.props.sourceTableId;
    const newName = database.props.title?.toString() ?? 'Untitled';

    // Already connected as lead → no-op
    if (existingInput === database.id) {
      console.log(
        '[AutomationConnectorTool] Database already connected as lead, skipping'
      );
      return;
    }

    // Already connected as secondary → no-op
    const inputTables = automation.props.inputTables ?? [];
    if (inputTables.some(t => t.tableId === database.id)) {
      console.log(
        '[AutomationConnectorTool] Database already connected as secondary, skipping'
      );
      return;
    }

    if (!existingInput) {
      // No lead → set as lead
      this.doc.transact(() => {
        automation.props.sourceTableId = database.id;
        automation.props.sourceTableName = newName;
        automation.props.sourceDocId = database.store?.id ?? null;
      });
      console.log(
        `[AutomationConnectorTool] Set LEAD: Database "${newName}" -> Automation "${automation.props.title}"`
      );
    } else {
      // Lead exists → add as secondary
      const id = Math.random().toString(36).slice(2, 10);
      this.doc.transact(() => {
        automation.props.inputTables = [
          ...inputTables,
          {
            id,
            tableId: database.id,
            tableName: newName,
            docId: database.store?.id ?? undefined,
            role: 'secondary' as const,
          },
        ];
      });
      console.log(
        `[AutomationConnectorTool] Added SECONDARY: Database "${newName}" -> Automation "${automation.props.title}"`
      );
    }
  }

  /**
   * Add an output table for an automation (Automation -> Database)
   * Multiple outputs are allowed.
   */
  private addOutputTable(
    automation: AutomationBlockModel,
    database: DatabaseBlockModel
  ): void {
    const outputs = automation.props.outputTables || [];

    // Check if already exists
    if (outputs.some(o => o.tableId === database.id)) {
      console.log(
        '[AutomationConnectorTool] Output table already exists, skipping'
      );
      return;
    }

    // Add the output table
    this.doc.transact(() => {
      automation.props.outputTables = [
        ...outputs,
        {
          tableId: database.id,
          tableName: database.props.title?.toString() ?? 'Untitled',
          docId: database.store?.id ?? null,
        },
      ];
    });

    console.log(
      `[AutomationConnectorTool] Added OUTPUT: Automation "${automation.props.title}" -> Database "${database.props.title}"`
    );
  }

  private resetAndReturn(preservePreview = false): void {
    // Remove any active preview edge
    if (!preservePreview) this.bridge?.removePreviewEdge();
    this.bridge?.setConnectorHighlights([]);

    this.startElement = null;
    this.startType = null;
    this.gfx.tool.setTool(DefaultTool);
  }
}
